import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider, ScrollRestoration } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BareLayout, Layout } from '@/components/Layout';
import { Formats } from '@/pages/Formats';
import { Register } from '@/pages/Register';
import { Success } from '@/pages/Success';
import { Login } from '@/pages/Login';
import { OAuthAuthorize } from '@/pages/OAuthAuthorize';
import { Verify } from '@/pages/Verify';
import { Me } from '@/pages/Me';
import { Invite } from '@/pages/Invite';
import { Team } from '@/pages/Team';
import { TeamJoin } from '@/pages/TeamJoin';
import { NotFound } from '@/pages/NotFound';
import { SiteLayout } from '@/components/site/SiteLayout';
import { Home } from '@/pages/site/Home';
import { Conditions } from '@/pages/site/Conditions';
import { Rules } from '@/pages/site/Rules';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { AdminRegistrations } from '@/pages/admin/AdminRegistrations';
import { AdminRegistration } from '@/pages/admin/AdminRegistration';
import { AdminReview } from '@/pages/admin/AdminReview';
import { AdminSettings } from '@/pages/admin/AdminSettings';
import { AdminFormats } from '@/pages/admin/AdminFormats';
import { AdminFormatEdit } from '@/pages/admin/AdminFormatEdit';
import { AdminVouchers } from '@/pages/admin/AdminVouchers';
import { AdminTeams } from '@/pages/admin/AdminTeams';
import { AdminTeam } from '@/pages/admin/AdminTeam';

// Stripe.js is only loaded on the payment step.
const Pay = lazy(() => import('@/pages/Pay').then((m) => ({ default: m.Pay })));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

/**
 * Every new page opens at the top; back/forward restores where the page was left. Keyed by
 * path, so a query change on the same page (admin filters, `?ref=`) keeps the position.
 */
function Root() {
  return (
    <>
      <ScrollRestoration getKey={(location) => location.pathname} />
      <Outlet />
    </>
  );
}

const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      {
        // Public site (gravelgames.fr): home, entry requirements, sporting rules. "Je m'engage"
        // opens the format selection at /formats.
        element: <SiteLayout />,
        children: [
          { path: '/', element: <Home /> },
          { path: '/conditions', element: <Conditions /> },
          { path: '/reglement', element: <Rules /> },
        ],
      },
      {
        // Checkout screens 01–03: no header or navigation (PRD §5), FR/EN switch only.
        element: <BareLayout languageSwitch />,
        children: [
          { path: '/formats', element: <Formats /> },
          { path: '/register', element: <Register /> },
          {
            path: '/pay',
            element: (
              <Suspense fallback={<p className="pt-13 text-center text-ink-muted">…</p>}>
                <Pay />
              </Suspense>
            ),
          },
        ],
      },
      {
        // Login (screen 05) carries its own switch.
        element: <BareLayout />,
        children: [{ path: '/login', element: <Login /> }],
      },
      {
        // Consent for the back-office MCP server (issue #47): signs the organiser in like /login.
        element: <BareLayout />,
        children: [{ path: '/oauth/authorize', element: <OAuthAuthorize /> }],
      },
      {
        // Join a team from the link (screen 07, P-21): full screen like checkout, FR/EN top right.
        element: <BareLayout languageSwitch />,
        children: [{ path: '/teams/join/:code', element: <TeamJoin /> }],
      },
      {
        // Back office: desktop layout, no consent banner, no analytics (organisers, not visitors).
        path: '/admin',
        element: <AdminLayout />,
        children: [
          { index: true, element: <AdminDashboard /> },
          { path: 'registrations', element: <AdminRegistrations /> },
          { path: 'registrations/:id', element: <AdminRegistration /> },
          { path: 'documents', element: <AdminReview /> },
          { path: 'settings', element: <AdminSettings /> },
          { path: 'formats', element: <AdminFormats /> },
          { path: 'formats/new', element: <AdminFormatEdit /> },
          { path: 'formats/:id', element: <AdminFormatEdit /> },
          { path: 'vouchers', element: <AdminVouchers /> },
          { path: 'teams', element: <AdminTeams /> },
          { path: 'teams/:id', element: <AdminTeam /> },
        ],
      },
      {
        element: <Layout />,
        children: [
          { path: '/success', element: <Success /> },
          { path: '/auth/verify', element: <Verify /> },
          { path: '/me', element: <Me /> },
          { path: '/me/invite', element: <Invite /> },
          { path: '/me/teams/:id', element: <Team /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
